import { Container, Skeleton, Stack } from '@everlywell/ui-kit';
import Layout from 'components/Layout';
import PDPHero from 'components/PageSections/PDPHero';
import Section from 'components/PDP/sections/Section';
import PurchaseOptionsDrawerProvider from 'components/PurchaseOptionsDrawerProvider';
import StructuredData from 'components/StructuredData';
import {
  structuredFaq,
  structuredOrgData,
  structuredProductData,
} from 'components/StructuredData/structuredDataHelpers';
import { PrePurchaseModalContextProvider } from 'contexts/PrePurchaseModal';
import { graphql, HeadProps, PageProps } from 'gatsby';
import { getSrc } from 'gatsby-plugin-image';
import React, { Suspense, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { APP_ROOT, PRECONNECT_LINKS } from 'utils/constants/urls';
import { isFullUrl, trackIterable } from 'utils/helpers';
import {
  calculateDiscountedPromoPrice,
  filterActivePromos,
  findActivePromotionForProduct,
  Promo,
} from 'utils/helpers/activePromoHelpers';
import { PDPPageReview } from 'utils/types';
import { notEmpty } from 'utils/types/utilityTypes';
import { v4 as uuidv4 } from 'uuid';

import FallBack from './[slug]';

const config = require('../../utils/constants/siteConfig');
export type ProductProps = {} & PageProps<Queries.PDPQuery>;

const Product = (props: ProductProps) => {
  const Pdp = props.data.allContentfulPdp001.nodes[0];
  const whatIsMeasuredData = props.data.whatsMeasured.nodes?.[0];
  const isLandingPage = hasContentfulFlag(Pdp, 'isLandingPage');
  const product = Pdp?.product;
  const spreeProduct = Pdp?.product?.spreeProduct;
  const productName = spreeProduct?.name;

  const contentfulFlags = Pdp?.metadata?.flags ?? [];

  const heroData = Pdp?.pageContents?.hero;

  const screenerData = Pdp?.pageContents?.screener ?? undefined;
  const disclaimerData = Pdp?.pageContents?.disclaimer ?? undefined;

  const shouldRenderPdp = product && spreeProduct && productName && heroData;

  // ### Analytics

  const [eventId] = useState(uuidv4());

  useEffect(() => {
    const price = spreeProduct?.variants?.find(
      (variant) => variant?.is_master,
    )?.price;

    if (eventId && spreeProduct?.productId && spreeProduct?.name && price) {
      trackIterable(eventId, {
        id: spreeProduct?.productId,
        name: spreeProduct?.name,
        price: parseFloat(price),
      });
    }
  }, [eventId, spreeProduct]);

  useEffect(() => {
    if (spreeProduct?.sku) {
      analytics.track({
        event: ANALYTICS.EVENTS.VIEWED_PAGE,
        data: {
          Page: 'PDP',
          sku: spreeProduct?.sku,
        },
      });
    }
  }, [spreeProduct]);

  const trackScrolledIntoView = (inView: boolean) => {
    if (inView && spreeProduct?.sku) {
      analytics.track({
        event: ANALYTICS.EVENTS.SCROLLED_TO_BOTTOM,
        data: {
          label: ANALYTICS.LABELS.PDP,
          sku: spreeProduct?.sku,
        },
      });
    }
  };

  const [footerRef] = useInView({
    triggerOnce: true,
    onChange: trackScrolledIntoView,
  });

  // ### Fallback

  if (!shouldRenderPdp) {
    return <FallBack />;
  }

  const sections = (Pdp.pageContents.sections ?? []).filter(notEmpty);

  return (
    <Layout isV2Page footerRef={footerRef} noNavbar={isLandingPage}>
      <PrePurchaseModalContextProvider>
        <PurchaseOptionsDrawerProvider>
          <Stack spacing={[12, 16, '8.75rem']} overflow="clip">
            <PDPHero
              heroData={heroData}
              whatIsMeasuredData={whatIsMeasuredData}
              product={product}
              screenerData={screenerData}
              disclaimerData={disclaimerData}
              isLandingPage={isLandingPage}
              contentfulFlags={contentfulFlags}
            />

            {sections?.map((sectionData) =>
              sectionData ? (
                <Suspense
                  key={sectionData.contentful_id}
                  fallback={
                    <Container size={['sm', 'sm', 'xl']}>
                      <Skeleton height={[400, null, 600]} width="100%" />
                    </Container>
                  }
                >
                  <Section data={sectionData} productData={product} />
                </Suspense>
              ) : null,
            )}
          </Stack>
        </PurchaseOptionsDrawerProvider>
      </PrePurchaseModalContextProvider>
    </Layout>
  );
};

export const Head = (props: HeadProps<Queries.PDPQuery>) => {
  const productData = props.data.allContentfulPdp001.nodes[0];
  const advertisedPromos = (props.data.allSpreePromotion.nodes ??
    []) as unknown as Promo[];
  const spreeProduct = productData?.product?.spreeProduct;
  const isLandingPage = hasContentfulFlag(productData, 'isLandingPage');
  const pageData = productData?.pageContents;
  const productVariant = spreeProduct?.variants?.find(
    (variant) => variant?.id === `${spreeProduct?.master_variant_id ?? 0}`,
  );

  const productId = spreeProduct?.productId ?? 0;
  const price = productVariant?.price ?? '0';
  const activePromo = findActivePromotionForProduct(
    productId,
    filterActivePromos(advertisedPromos),
  );

  const discountedPromoPrice = calculateDiscountedPromoPrice(
    price,
    activePromo,
  );

  if (!pageData?.seo) {
    // log SEO is missing for this page
    return null;
  }

  const hasNoIndexKeyword = pageData.seo.metaKeywords?.includes('noindex');
  // Remove trailing slash from the URL if it exists
  const seoSlug = pageData.seo?.canonicalUrl?.replace(/\/$/, '');
  const canonicalUrl = `products/${seoSlug}`;
  const faqSchema = pageData.seo.faqSchema?.map((section) => ({
    question: section?.question ?? '',
    answer: section?.answer ?? '',
  }));

  const outOfStock =
    productData.metadata?.flags?.find(
      (flag) => flag?.metadata?.metadata_id === 'outOfStock',
    )?.value === true;

  const productImageData =
    productData.product?.images?.[0]?.desktopImage?.gatsbyImageData;

  const productImageSrc = productImageData ? getSrc(productImageData) : '';
  const productDescription = pageData.seo.metaDescription?.metaDescription;
  const basePrice = discountedPromoPrice ? discountedPromoPrice.toFixed(2) : '';

  const structuredProductSchema = spreeProduct
    ? structuredProductData(
        {
          productId: spreeProduct.productId ?? 0,
          name: spreeProduct.name ?? '',
          slug: spreeProduct.slug ?? '',
          sku: spreeProduct.sku ?? '',
          upc: spreeProduct.upc ?? '',
          price: productVariant?.price ?? '',
          powerReviews: {
            average_rating: spreeProduct.powerReviews?.average_rating ?? 0,
            review_count: spreeProduct.powerReviews?.review_count ?? 0,
            rating_histogram: (spreeProduct.powerReviews?.rating_histogram ??
              []) as number[],
            recommended_ratio:
              spreeProduct.powerReviews?.recommended_ratio ?? 0,
            contents: (spreeProduct.powerReviews?.contents ??
              []) as PDPPageReview[],
          },
          outOfStock,
          promotionable: spreeProduct.promotionable ?? false,
        },
        productImageSrc ?? '',
        productDescription ?? '',
        basePrice,
        activePromo,
      )
    : undefined;

  return (
    <>
      {isLandingPage ? (
        // Hide the chatbot button
        <style>
          {`#livesdk__campaign, #livesdk__campaign * {
            display: none !important;
          }`}
        </style>
      ) : null}
      {PRECONNECT_LINKS.map((link: string, index) => (
        <link
          key={index}
          href={link}
          rel="preconnect"
          crossOrigin="anonymous"
        />
      ))}

      <>
        {/* General tags */}
        <title>{pageData.seo.metaTitle || config.title}</title>
        <meta
          name="description"
          content={
            pageData.seo.metaDescription?.metaDescription ||
            config.siteDescription
          }
        />
        <meta
          name="keywords"
          content={pageData.seo.metaKeywords || config.keywords}
        />
        {/* Canonical URL is a Contentful field available in the Pages model.
        If the field is blank, Gastby Helmet plugin will take over and generate the
        Canonical URL based on the page slug.
        */}
        {canonicalUrl && !isFullUrl(canonicalUrl) ? (
          <link
            rel="canonical"
            key={`${config.siteUrl}/${canonicalUrl}/`}
            href={`${config.siteUrl}/${canonicalUrl}/`}
          />
        ) : canonicalUrl ? (
          <link
            rel="canonical"
            key={`${canonicalUrl}/`}
            href={`${canonicalUrl}/`}
          />
        ) : (
          []
        )}
        {/* OpenGraph tags */}
        <meta
          property="og:title"
          content={pageData.seo.metaTitle || config.title}
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content={`${APP_ROOT}/products/${seoSlug}/` || config.siteUrl}
        />
        <meta
          property="og:description"
          content={
            pageData.seo.metaDescription?.metaDescription ||
            config.siteDescription
          }
        />
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:creator"
          content={config.userTwitter ? config.userTwitter : ''}
        />
        <meta
          name="twitter:title"
          content={pageData.seo.metaTitle || config.siteTitle}
        />
        <meta
          name="twitter:description"
          content={
            pageData.seo.metaDescription?.metaDescription ||
            config.siteDescription
          }
        />
        {/* Disable iPhone auto zoom on select drop down menus */}
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1"
        ></meta>
        {hasNoIndexKeyword ? (
          <meta name="robots" content="noindex, nofollow, noimageindex" />
        ) : null}
        {/* Schema.org tags */}
        {/* Organization Schema Attributes from config */}
        <StructuredData data={structuredOrgData} />
        {/* FAQ Schema Attributes from Contentful */}
        {faqSchema?.length ? (
          <StructuredData data={structuredFaq(faqSchema)} />
        ) : null}
        {/* Product structured data */}
        {structuredProductSchema ? (
          <StructuredData data={structuredProductSchema} />
        ) : null}
      </>
    </>
  );
};

export const query = graphql`
  fragment pdpMedia001 on ContentfulMedia001 {
    desktopImage {
      gatsbyImageData(placeholder: BLURRED)
    }
    mobileImage {
      gatsbyImageData(placeholder: BLURRED)
    }
    desktopImageAltText
    mobileImageAltText
    iconFile {
      filename
      file {
        url
      }
    }
  }
  fragment pdpContentfulMetadata001 on ContentfulMetadata001 {
    title
    metadata_id
    description {
      description
    }
    theme {
      backgroundColor
    }
    context {
      eyebrow
      title
    }
    ctas {
      label
      url
    }
    flags {
      value
      metadata {
        metadata_id
      }
    }
  }

  fragment pdpContentfulSection001 on ContentfulSection001 {
    isContentfulSection001
    contentful_id
    displayName
    componentName
    eyebrow
    body {
      raw
    }
    metadata {
      ...pdpContentfulMetadata001
    }
    images {
      ...pdpMedia001
    }
    body {
      raw
      references {
        ... on ContentfulAsset {
          gatsbyImageData(placeholder: BLURRED)
          contentful_id
        }
      }
    }
  }

  fragment pdpSubsections on ContentfulSection001 {
    ...pdpContentfulSection001
    subsections {
      ...pdpContentfulSection001
      ... on ContentfulSection001 {
        subsections {
          ...pdpContentfulSection001
          ... on ContentfulSection001 {
            subsections {
              ...pdpContentfulSection001
              ... on ContentfulSection001 {
                subsections {
                  ...pdpContentfulSection001
                  ... on ContentfulSection001 {
                    subsections {
                      ...pdpContentfulSection001
                      ... on ContentfulSection001 {
                        subsections {
                          ...pdpContentfulSection001
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
      ... on ContentfulCarousel001 {
        isContentfulCarousel001
        items {
          ... on ContentfulCarouselItem001 {
            content {
              subsections {
                ... on ContentfulProduct001 {
                  slug
                }
              }
            }
          }
        }
      }
    }
  }

  fragment pdpSection on ContentfulSection001 {
    metadata {
      ...pdpContentfulMetadata001
    }
    ...pdpSubsections
  }

  fragment pdpHero on ContentfulHero001 {
    contentful_id
    metadata {
      ...pdpContentfulMetadata001
    }
    images {
      ...pdpMedia001
    }
    sections {
      ...pdpSection
    }
  }

  fragment pdpProductFlags on ContentfulFlag001 {
    value
    displayText
    metadata {
      metadata_id
    }
  }

  fragment pdpProductMetadata on ContentfulMetadata001 {
    context {
      outOfStock
      hideRating
    }
    flags {
      ...pdpProductFlags
    }
  }

  fragment pdpContentfulProduct on ContentfulProduct001 {
    slug
    images {
      ...pdpMedia001
    }
    metadata {
      ...pdpProductMetadata
    }
    spreeProduct {
      ...pdpSpreeProduct
    }
    comparisonDescription {
      raw
    }
  }

  fragment pdpProduct on ContentfulProduct001 {
    ...pdpContentfulProduct
    linkedProducts {
      ...pdpContentfulProduct
    }
  }

  fragment pdpSpreeProductVariant on SpreeVariant {
    is_master
    subscription_plan {
      id
      name
      interval
      interval_count
      percent_discount
    }
    price
    id
    sku
  }

  fragment pdpSpreeProduct on SpreeProduct {
    powerReviews {
      average_rating
      product_page_id
      review_count
      recommended_ratio
      average_rating
      rating_histogram
      contents {
        headline
        comments
        created_date
        author
        rating
        location
      }
    }
    master_variant_id
    max_dtc_quantity
    meta_description
    meta_keywords
    meta_title
    name
    productId
    promotionable
    sku
    slug
    upc
    variants {
      ...pdpSpreeProductVariant
    }
  }

  fragment pdpScreener on ContentfulScreener001 {
    displayName
    options {
      displayName
      items
      eligibility {
        value
      }
    }
    metadata {
      ...pdpContentfulMetadata001
    }
  }

  fragment pdpDisclaimer on ContentfulDisclaimer {
    body {
      raw
    }
    title
    callToAction {
      text
      url
    }
  }
  query PDP($pageContents__slug: String) {
    allContentfulPdp001(
      filter: { pageContents: { slug: { eq: $pageContents__slug } } }
    ) {
      nodes {
        displayName
        metadata {
          ...pdpContentfulMetadata001
        }
        pageContents {
          slug
          hero {
            ...pdpHero
          }
          sections {
            ...pdpSection
          }
          seo {
            metaTitle
            metaDescription {
              metaDescription
            }
            metaKeywords
            faqSchema {
              question
              answer
            }
            canonicalUrl
          }
          displayName
          screener {
            ...pdpScreener
          }
          disclaimer {
            ...pdpDisclaimer
          }
        }
        product {
          ...pdpProduct
        }
      }
    }

    ## This query is used to get the content for the "What's Measured" modal
    whatsMeasured: allContentfulContentSection(
      filter: {
        slug: { eq: "whatsMeasured" }
        page_section_v2: {
          elemMatch: {
            componentName: { eq: "DetailsSection" }
            pdp_page: {
              elemMatch: { product: { slug: { eq: $pageContents__slug } } }
            }
          }
        }
      }
      sort: { createdAt: DESC }
      limit: 1
    ) {
      nodes {
        id
        slug
        modalTitle
        modalButtonText
        listOfContentSections {
          title
          description {
            description
          }
          image {
            gatsbyImageData
            file {
              url
            }
          }
        }
      }
    }

    ## This query is used to get the all available promotions for SEO offer
    allSpreePromotion {
      nodes {
        code
        description
        rules {
          included_product_ids
          excluded_product_ids
        }
        discounts {
          flat_percent
          tiered {
            price_range
            percent
          }
        }
        id
        expires_at
        starts_at
      }
    }
  }
`;

export default Product;

/**
 * Check if the PDP is a landing page or not
 * @param data
 * @returns
 */
const hasContentfulFlag = (
  data: Queries.PDPQuery['allContentfulPdp001']['nodes'][0],
  metadataId: string,
) =>
  data?.metadata?.flags
    ?.filter(notEmpty)
    .find((flag) => flag.metadata?.metadata_id === metadataId)?.value === true;
